import { useCallback, useEffect, useState } from 'react';

type Updater<T> = T | ((prev: T) => T);

function isUpdaterFn<T>(value: Updater<T>): value is (prev: T) => T {
    return typeof value === 'function';
}

function readFromStorage<T>(storage: Storage, key: string, init: T): T {
    const raw = storage.getItem(key);
    if (raw === null) {
        return init;
    }
    try {
        return JSON.parse(raw) as T;
    } catch {
        return init;
    }
}

interface UseBrowserStorageOptions {
    storage: Storage;
    area: 'local' | 'session';
    getKey: (storageKey: string) => string;
}

export function useBrowserStorage<T>(
    storageKey: string,
    init: T,
    { storage, area, getKey }: UseBrowserStorageOptions
): [T, (updater: Updater<T>) => void] {
    const key = getKey(storageKey);
    const eventType = `${area}-storage-update-${key}`;

    const [cache, setCache] = useState(() => ({ storageKey, value: readFromStorage<T>(storage, key, init) }));

    // Resync during render (not via an effect) whenever the key changes, so a stale cache for a
    // previously-active key is never trusted — e.g. after another tab wrote to that key while this
    // hook was parked on a different one. React discards this render and re-renders synchronously
    // with the fresh value, so this doesn't cost an extra committed render.
    let state = cache.value;
    if (cache.storageKey !== storageKey) {
        state = readFromStorage<T>(storage, key, init);
        setCache({ storageKey, value: state });
    }

    const replace = useCallback(
        (updater: Updater<T>) => {
            const prevValue = readFromStorage<T>(storage, key, init);
            const nextValue = isUpdaterFn(updater) ? updater(prevValue) : updater;
            storage.setItem(key, JSON.stringify(nextValue));
            window.dispatchEvent(new CustomEvent(eventType, { detail: nextValue }));
        },
        [storage, key, init, eventType]
    );

    useEffect(() => {
        if (area !== 'local') {
            return undefined;
        }

        const handleStorageEvent = (event: StorageEvent) => {
            if (event.storageArea === storage && event.key === key) {
                const value = readFromStorage<T>(storage, key, init);
                window.dispatchEvent(new CustomEvent(eventType, { detail: value }));
            }
        };

        window.addEventListener('storage', handleStorageEvent);
        return () => window.removeEventListener('storage', handleStorageEvent);
    }, [storage, key, init, eventType, area]);

    useEffect(() => {
        const handleStateUpdateEvent = (event: CustomEventInit<T>) => {
            setCache({ storageKey, value: event.detail as T });
        };

        window.addEventListener(eventType, handleStateUpdateEvent);
        return () => window.removeEventListener(eventType, handleStateUpdateEvent);
    }, [eventType, storageKey]);

    return [state, replace];
}
